<script setup lang="ts">
import { useMediaQuery } from "@vueuse/core";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import SidebarMenu from "@/components/MyStudents/Navigation/SidebarMenu.vue";
import TopMenu from "@/components/MyStudents/Navigation/TopMenu.vue";
import CampusInvolvementSection from "@/components/OperationsApp/components/Navigation/CampusInvolvementSection/index.vue";
import CommunityBasedLearningSection from "@/components/OperationsApp/components/Navigation/CommunityBasedLearningSection/index.vue";
import FulfilmentReportsSection from "@/components/OperationsApp/components/Navigation/FulfilmentReportsSection/index.vue";
import HomeSection from "@/components/OperationsApp/components/Navigation/HomeSection/index.vue";
import LibrarySection from "@/components/OperationsApp/components/Navigation/LibrarySection/index.vue";
import ProgramReportsSection from "@/components/OperationsApp/components/Navigation/ProgramReportsSection/index.vue";
import StudentFulfillmentSection from "@/components/OperationsApp/components/Navigation/StudentFulfillmentSection/index.vue";
import BreadCrumb from "@/components/shared/BreadCrumb.vue";
import {
  hasHiddenNavItemsEnabled,
  hasPriceOverrideApprovalsAccess,
  hasProgramsReadPermission,
  hasStudentFulfillmentPermission,
  usePriceOverrides,
} from "@/composables/authorization";
import { pagePresets } from "../../styles.ts";

defineOptions({ name: "Operations" });

const route = useRoute();
const store = useStore();

const isLargeScreen = useMediaQuery("(max-width: 1200px) and (min-width: 620px)");

const classWidth = computed(() => (route?.meta?.showSideMenu ? "calc(100vw - 465px)" : "calc(100vw - 100px)"));

const showHiddenNav = hasHiddenNavItemsEnabled;

const priceOverrideApprovalsAccess = hasPriceOverrideApprovalsAccess && usePriceOverrides.value;
const studentFulfillmentPermission = hasStudentFulfillmentPermission;

store.dispatch("programManager/setAllCounts");

const countCards = computed(() => store.getters["programManager/getCards"]);

const counts = computed(() => {
  const counts = new Map<string, number>();
  for (const countCard of countCards.value) {
    if (countCard?.subcategories) {
      for (const subcategory of countCard.subcategories) {
        counts.set(subcategory.route, subcategory.count);
      }
    }
  }
  return counts;
});
</script>

<template>
  <div>
    <div class="w-full h-full bg-blue-100">
      <div class="flex flex-col bg-gray-50 min-h-screen" data-testid="price-override-layout">
        <div v-if="route?.meta?.showTopMenu || isLargeScreen" class="flex flex-row items-center justify-between pt-4 ml-4">
          <div v-if="route?.meta?.title" class="w-96 font-[Montserrat] font-semibold text-[24px] leading-[30px]">{{ route?.meta?.title }}</div>
          <BreadCrumb v-if="route?.meta?.showTopMenu"/>
          <top-menu
            class="!pt-0 z-50"
            :show-popup-menu="!route?.meta?.showSideMenu || isLargeScreen"
            data-test-id="top-menu-program-manager"
          >
            <template #sections>
              <HomeSection v-if="showHiddenNav" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <LibrarySection v-if="hasProgramsReadPermission" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <CampusInvolvementSection v-if="showHiddenNav" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <CommunityBasedLearningSection v-if="showHiddenNav" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <StudentFulfillmentSection v-if="studentFulfillmentPermission || priceOverrideApprovalsAccess" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <ProgramReportsSection v-if="showHiddenNav" :show-hidden-nav="showHiddenNav" :show-header="true" />
              <FulfilmentReportsSection v-if="showHiddenNav" :show-hidden-nav="showHiddenNav" :show-header="true" />
            </template>
          </top-menu>
        </div>
        <section class="flex flex-col lg:flex-row">
          <sidebar-menu
            v-if="!!route?.meta?.showSideMenu && !isLargeScreen"
            class="block min-w-[325px] !h-100 max-lg:mx-auto md:min-h-fit"
            title="Program Manager"
            data-test-id="sidebar-menu-program-manager"
          >
            <template #sidebarSections>
              <Accordion :activeIndex="[0, 1]" :multiple="true" class="h-full overflow-y-scroll" :pt="pagePresets.accordion">
                <AccordionTab v-if="showHiddenNav" header="Home" :pt="pagePresets.accordion">
                  <HomeSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="hasProgramsReadPermission" header="Library" :pt="pagePresets.accordion">
                  <LibrarySection :counts="counts" :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="showHiddenNav" header="Campus Involvement" :pt="pagePresets.accordion">
                  <CampusInvolvementSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="showHiddenNav" header="Community Based Learning" :pt="pagePresets.accordion">
                  <CommunityBasedLearningSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="studentFulfillmentPermission || priceOverrideApprovalsAccess" header="Student Fulfillment" :pt="pagePresets.accordion">
                  <StudentFulfillmentSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="showHiddenNav" header="Program Reports" :pt="pagePresets.accordion">
                  <ProgramReportsSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
                <AccordionTab v-if="showHiddenNav" header="Fulfillment Reports" :pt="pagePresets.accordion">
                  <FulfilmentReportsSection :show-hidden-nav="showHiddenNav" />
                </AccordionTab>
              </Accordion>
            </template>
          </sidebar-menu>
          <div class="w-full max-lg:px-8 main-panel-width self-start">
            <router-view />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.main-panel-width {
  @media (min-width: 1200px) {
    width: v-bind(classWidth)
  }
}
</style>
